import type {
  NextPage,
  GetStaticPropsResult,
  GetStaticPropsContext,
} from 'next';
import { ParsedUrlQuery } from 'querystring';
import NextError from 'next/error';
import { useRouter } from 'next/router';
import slugify from 'react-slugify';
import Head from 'next/head';
import { GetStaticPathsResult } from 'next';
import Profile from '@/components/Profile/Profile';
import Collection from '@/components/Stream/Collection';
import Post from '@/components/Stream/Post';
import PostEdit from '@/components/Stream/PostEdit';
import {
  getCollection,
  getPost,
  getUserByUsername,
  getIndexedPosts,
  getCollectionActivities,
} from '@/firebase/streamSSR';
import { useAuth } from '@/providers/AuthProvider';
import { postUrl } from '@/lib/utils';
import { IPost } from '@/types/Collections/posts';
import { IProfile, ProfilePage } from '@/types/Collections/users';
import {
  ICollection,
  ICollectionActivity,
} from '@/types/Collections/collections';

/*
 * This is basically a catch-all page,
 * that should either route to a post,
 * or a profile, or a collection
 * if not, it should go to a 404
 */

interface IProfileAndPostProps {
  postId?: string | null;
  post?: string | null;
  collection?: string | null;
  activities?: string | null;
}

const ProfileAndPost: NextPage = (props: IProfileAndPostProps) => {
  const { postId, post, collection, activities } = props;

  const { authLoaded, isLoggedIn } = useAuth();

  const router = useRouter();
  const { slug } = router.query;
  let profilePage = 'posts' as ProfilePage;

  const slugparts = [...(slug as string[])];

  // If the first part of the URL doesn't begin with @ then this should 404
  const decodedSlug = decodeURIComponent(slugparts[0]);
  if (!slugparts.length || decodedSlug.substring(0, 1) !== '@') {
    return <NextError statusCode={404} />;
  }

  if (post) {
    const data = JSON.parse(post);
    if (authLoaded && isLoggedIn && postId) {
      return (
        <>
          <Head>
            <title>{`${data?.title} - Shift.ms`}</title>
            {data && data.author && (
              <link
                rel="canonical"
                href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}${postUrl(data)}`}
              />
            )}
            {data && data.author && (
              <meta
                property="article:author"
                key="article:author"
                content={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/@${data.author.username}`}
              />
            )}
            <meta property="og:type" content="article" key="og:type" />
            <meta property="og:title" content={data?.title} key="og:title" />
            <meta
              property="og:description"
              content={data?.content?.substring(0, 150)}
              key="og:description"
            />
            {data && data.author && (
              <meta
                property="og:url"
                key="og:url"
                content={`${process.env.NEXT_PUBLIC_APP_DOMAIN}${postUrl(
                  data,
                )}`}
              />
            )}
            {data && !data.isIndexed ? (
              <meta name="robots" content="noindex" key="robots" />
            ) : (
              ''
            )}
            {data && data.image ? (
              <meta property="og:image" key="og:image" content={data.image} />
            ) : (
              <meta
                property="og:image"
                key="og:image"
                content={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/api/postimage/${postId}`}
              />
            )}
          </Head>
          <Post initialData={data} postId={postId} />
        </>
      );
    }
    if (data && data.id) {
      return (
        <>
          <Head>
            <title>{`${data?.title} - Shift.ms`}</title>
            <link
              rel="canonical"
              href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}${postUrl(data)}`}
            />
            <meta
              property="article:author"
              key="article:author"
              content={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/@${data.author.username}`}
            />
            <meta property="og:type" content="article" key="og:type" />
            <meta property="og:title" content={data.title} key="og:title" />
            <meta
              property="og:description"
              content={data?.content?.substring(0, 150)}
              key="og:description"
            />
            <meta
              property="og:url"
              key="og:url"
              content={`${process.env.NEXT_PUBLIC_APP_DOMAIN}${postUrl(data)}`}
            />
            {data && data.image ? (
              <meta property="og:image" key="og:image" content={data.image} />
            ) : (
              <meta
                property="og:image"
                key="og:image"
                content={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/api/postimage/${postId}`}
              />
            )}
            {!data.isIndexed ? (
              <meta name="robots" content="noindex" key="robots" />
            ) : (
              ''
            )}
          </Head>
          <Post initialData={data} postId={data.id} />
        </>
      );
    }
    return <NextError statusCode={404} />;
  }

  if (collection) {
    const data = JSON.parse(collection);
    const activitiesData = JSON.parse(activities || '');
    return <Collection collection={data} activities={activitiesData} />;
  }

  if (slugparts.length === 3 && slugparts[2] === 'edit') {
    return <PostEdit />;
  }

  if (slugparts.length === 2) {
    [, profilePage] = slugparts as ProfilePage[];
  }
  return <Profile username={decodedSlug.substring(1)} page={profilePage} />;
};

ProfileAndPost.defaultProps = {
  postId: null,
  post: null,
  collection: null,
  activities: null,
};

export default ProfileAndPost;

interface IPageParams extends ParsedUrlQuery {
  slug: string[];
}

export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<IProfileAndPostProps>> {
  const { slug } = context.params as IPageParams;

  const slugparts = [...slug];

  if (slugparts.length === 3) {
    // This is a collection
    if (slug[1] === 'collections') {
      let [username] = slug;
      if (slug[0].substring(0, 1) === '@') {
        username = slug[0].substring(1);
      }

      const [, , collectionslug] = slug;
      const collectionslugparts = collectionslug.split('-');
      let collectionId = collectionslugparts[collectionslugparts.length - 1];
      if (slugparts[1].slice(slugparts[1].length - 5) === '.json') {
        collectionId = slugparts[1].substring(0, slugparts[1].indexOf('.'));
      }

      const user = (await getUserByUsername(username)) as IProfile;

      const collection: ICollection = (await getCollection(
        username,
        collectionId,
        user,
      )) as ICollection;
      if (collection) {
        let activities: (ICollectionActivity | null)[] | null = [];
        if (!collection.isPrivate && collection.author?.uid) {
          activities = await getCollectionActivities(
            collection.author?.uid,
            collection.id,
          );
        }
        return {
          props: {
            collection: JSON.stringify(collection),
            activities: activities
              ? JSON.stringify(activities.filter(a => a))
              : null,
          },
          revalidate: 60 * 60 * 24, // one day
        };
      }
    }
    // Edit post?
    if (slugparts[2] === 'edit' || slugparts[2] === 'edit.json') {
      // Just return nothing, we'll do it client side.
      return { props: {} };
    }
    return { props: {} };
  }

  // This means this is a post, which we should fetch data for as we need for SEO
  if (slugparts[1]) {
    switch (slugparts[1]) {
      // Reserved profile routes
      case 'followers':
      case 'following':
      case 'about':
      case 'collections':
      case 'scheduled':
        break;

      default: {
        let post = null;
        const [username, postslug] = slugparts;
        const postslugparts = postslug.split('-');
        let postId = postslugparts[postslugparts.length - 1];
        if (slugparts[1].slice(slugparts[1].length - 5) === '.json') {
          postId = slugparts[1].substring(0, slugparts[1].indexOf('.'));
        }
        if (postId) {
          const data = (await getPost(postId)) as IPost;
          // Redirect to SEO friendly version if necessary
          if (data) {
            let canonicalSlug = slugify(data.title);
            canonicalSlug = canonicalSlug.substring(0, 64);
            canonicalSlug = `${canonicalSlug}-${postId}`;
            if (postslugparts.length < 2 || postslug !== canonicalSlug) {
              return {
                redirect: {
                  destination: `/@${username}/${canonicalSlug}`,
                  permanent: true,
                },
              };
            }
          }
          post = JSON.stringify(data);
        }
        return {
          props: {
            postId,
            post,
          },
          revalidate: 60 * 5, // five minutes
        };
      }
    }
  }
  return { props: {} };
}

// The only pages we're going to pre-build are
// the posts that should be indexed.
export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  const paths = await getIndexedPosts();
  return {
    paths,
    fallback: 'blocking',
  };
}
